import React, { useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    List,
    ListItemButton,
    ListItemText,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { IConcertSeriesAdminDto } from '../../clients/services';

type EditorMode = 'edit' | 'view';

interface SeriesListPanelProps {
    series: IConcertSeriesAdminDto[];
    selectedSeriesId?: number;
    onSelectSeries: (seriesId: number) => void;
    onCreateSeries: (title: string) => Promise<void>;
    editorMode?: EditorMode;
    onEditorModeChange?: (mode: EditorMode) => void;
}

const formatRange = (series: IConcertSeriesAdminDto): string => {
    const start = series.seriesStart ? series.seriesStart.toFormat('dd.MM.yyyy') : '-';
    const end = series.seriesEnd ? series.seriesEnd.toFormat('dd.MM.yyyy') : '-';
    return `${start} - ${end}`;
};

const sortSeries = (series: IConcertSeriesAdminDto[]): IConcertSeriesAdminDto[] => {
    return [...series].sort((a, b) => {
        const aDate = a.seriesStart ? a.seriesStart.toMillis() : 0;
        const bDate = b.seriesStart ? b.seriesStart.toMillis() : 0;
        return bDate - aDate;
    });
};

const compactAdminFormSx = {
    '& .MuiTextField-root .MuiInputBase-input': {
        fontSize: '0.86rem',
        paddingTop: '7px',
        paddingBottom: '7px',
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.82rem',
    },
    '& .MuiButton-root': {
        fontSize: '0.8rem',
        paddingTop: '4px',
        paddingBottom: '4px',
        minHeight: 30,
    },
    '& .MuiTypography-h6': {
        fontSize: '1rem',
    },
};

export const SeriesListPanel: React.FC<SeriesListPanelProps> = ({
    series,
    selectedSeriesId,
    onSelectSeries,
    onCreateSeries,
    editorMode,
    onEditorModeChange,
}) => {
    const [query, setQuery] = useState('');
    const [newSeriesTitle, setNewSeriesTitle] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState('');

    const filteredSeries = useMemo(() => {
        const normalizedQuery = query.toLowerCase().trim();
        const sorted = sortSeries(series);
        if (!normalizedQuery) {
            return sorted;
        }

        return sorted.filter((item) => (item.title || '').toLowerCase().includes(normalizedQuery));
    }, [series, query]);

    return (
        <Box
            sx={{
                p: 1.25,
                pt: 1.75,
                borderRight: '1px solid',
                borderColor: 'divider',
                height: '100%',
                overflow: 'auto',
                backgroundColor: '#ffffff',
                color: '#1f1f1f',
                position: 'relative',
                ...compactAdminFormSx,
            }}
        >
            {editorMode && onEditorModeChange && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        zIndex: 2,
                    }}
                >
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={editorMode}
                        onChange={(_, value: EditorMode | null) => {
                            if (value) {
                                onEditorModeChange(value);
                            }
                        }}
                    >
                        <ToggleButton value="edit">Edit</ToggleButton>
                        <ToggleButton value="view">View</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            )}

            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                <TextField
                    size="small"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Suchen"
                    fullWidth
                />
            </Stack>

            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <TextField
                    size="small"
                    value={newSeriesTitle}
                    onChange={(event) => setNewSeriesTitle(event.target.value)}
                    placeholder="Neue Konzertserie"
                    fullWidth
                />
                <Button
                    variant="contained"
                    disabled={isCreating || newSeriesTitle.trim().length === 0}
                    onClick={async () => {
                        setIsCreating(true);
                        setError('');
                        try {
                            await onCreateSeries(newSeriesTitle.trim());
                            setNewSeriesTitle('');
                        } catch (e) {
                            setError('Serie konnte nicht erstellt werden.');
                        } finally {
                            setIsCreating(false);
                        }
                    }}
                >
                    Neu
                </Button>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                </Alert>
            )}

            <List dense disablePadding>
                {filteredSeries.map((item) => {
                    const isSelected = selectedSeriesId === item.id;
                    return (
                        <ListItemButton
                            key={item.id}
                            selected={isSelected}
                            sx={{
                                mb: 1,
                                border: '1px solid',
                                borderColor: isSelected ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                alignItems: 'flex-start',
                                gap: 1,
                            }}
                            onClick={() => onSelectSeries(item.id)}
                        >
                            <Box
                                sx={{
                                    width: 50,
                                    minWidth: 150,
                                    height: 'auto',
                                    alignItems: 'middle',
                                    backgroundColor: 'grey.200',
                                    borderRadius: 0.5,
                                    overflow: 'hidden',
                                }}
                            >
                                {!!item.banner?.url && (
                                    <Box
                                        component="img"
                                        src={item.banner.url}
                                        alt={item.banner.title || item.title}
                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                )}
                            </Box>
                            <ListItemText
                                primary={item.title}
                                secondary={formatRange(item)}
                                primaryTypographyProps={{ fontWeight: 600 }}
                            />
                            <Chip
                                size="small"
                                color={item.hide ? 'warning' : 'success'}
                                label={item.hide ? 'Versteckt' : 'Publiziert'}
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        </Box>
    );
};
